import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

export interface RowAction {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

// "⋯" overflow menu: keeps destructive actions out of the row's tap targets.
const RowActionsMenu: React.FC<{ actions: RowAction[]; label: string; size?: number }> = ({ actions, label, size = 44 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(false); ref.current?.querySelector('button')?.focus(); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="btn-s ghost icon"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{ minWidth: size, minHeight: size, touchAction: 'manipulation' }}
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: '100%', zIndex: 20, minWidth: 180, padding: 4,
          background: 'var(--surface, #fff)', border: '1px solid var(--line)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,.12)',
        }}>
          {actions.map(a => (
            <button
              key={a.label}
              role="menuitem"
              type="button"
              onClick={() => { setOpen(false); a.onClick(); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', minHeight: 40,
                background: 'none', border: 0, borderRadius: 6, cursor: 'pointer', font: 'inherit',
                color: a.danger ? 'var(--danger)' : 'var(--ink)',
              }}
            >{a.label}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RowActionsMenu;
